import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/app/AppShell';
import {
  useTodayAttendance, useMyAttendance, useClockIn, useClockOut,
} from '@/lib/attendance';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function hhmm(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function hours(mins: number | null): string {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function AttendancePage() {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const today = useTodayAttendance();
  const monthData = useMyAttendance(year, month);
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const [breakMin, setBreakMin] = useState('0');
  const [workCountry, setWorkCountry] = useState('');

  const rec = today.data?.record;
  const isIn = !!rec?.clockInAt && !rec?.clockOutAt;
  const isDone = !!rec?.clockInAt && !!rec?.clockOutAt;

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-5xl">
        <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('attendance.title', { defaultValue: 'Time clock' })}</h1>
        <p className="mt-1 text-sm text-ink-500">{t('attendance.subtitle', { defaultValue: '勤怠 · Clock in and out; monthly hours feed the 36協定 overtime check.' })}</p>

        {/* Clock widget */}
        <section className="mt-6 rounded-2xl border border-ink-200 bg-white p-6 flex flex-wrap items-center gap-6">
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold">{today.data?.date ?? '—'}</p>
            <p className="mt-1 text-2xl font-bold text-ink-900">
              {isIn ? t('attendance.clockedIn', { defaultValue: 'Clocked in' }) : isDone ? t('attendance.doneForToday', { defaultValue: 'Done for today' }) : t('attendance.notClockedIn', { defaultValue: 'Not clocked in' })}
            </p>
            <p className="mt-1 text-sm text-ink-600">
              {t('attendance.inLabel', { defaultValue: 'In' })} {hhmm(rec?.clockInAt ?? null)} · {t('attendance.outLabel', { defaultValue: 'Out' })} {hhmm(rec?.clockOutAt ?? null)}
              {rec?.workedMinutes != null && <> · {t('attendance.workedLabel', { defaultValue: 'Worked' })} {hours(rec.workedMinutes)}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isIn && !isDone && (
              <div className="flex items-end gap-2">
                <label className="text-xs text-ink-600">
                  <span className="block mb-1">{t('attendance.workLocation', { defaultValue: 'Work location' })}</span>
                  <input
                    value={workCountry}
                    onChange={(e) => setWorkCountry(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder={t('attendance.workLocationPlaceholder', { defaultValue: 'e.g. JP' })}
                    title={t('attendance.workLocationHint', { defaultValue: "2-letter country code where you're working today (for cross-border residency tracking)" })}
                    className="w-20 border border-ink-300 rounded-lg px-2 py-2 text-sm uppercase focus:border-accent-500 focus:outline-none"
                  />
                </label>
                <button
                  onClick={() => clockIn.mutate({ workCountry: workCountry || undefined })}
                  disabled={clockIn.isPending}
                  className="inline-flex h-12 items-center px-6 rounded-xl bg-accent-500 text-white text-base font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
                >
                  {clockIn.isPending ? t('attendance.clockingIn', { defaultValue: 'Clocking in…' }) : t('attendance.clockIn', { defaultValue: '🟢 Clock in' })}
                </button>
              </div>
            )}
            {isIn && (
              <div className="flex items-end gap-2">
                <label className="text-xs text-ink-600">
                  <span className="block mb-1">{t('attendance.breakMin', { defaultValue: 'Break (min)' })}</span>
                  <input type="number" min={0} value={breakMin} onChange={(e) => setBreakMin(e.target.value)}
                    className="w-20 border border-ink-300 rounded-lg px-2 py-2 text-sm focus:border-accent-500 focus:outline-none" />
                </label>
                <button
                  onClick={() => clockOut.mutate(Number(breakMin) || 0)}
                  disabled={clockOut.isPending}
                  className="inline-flex h-12 items-center px-6 rounded-xl bg-ink-900 text-white text-base font-semibold hover:bg-ink-800 disabled:opacity-50 transition"
                >
                  {clockOut.isPending ? t('attendance.clockingOut', { defaultValue: 'Clocking out…' }) : t('attendance.clockOut', { defaultValue: '🔴 Clock out' })}
                </button>
              </div>
            )}
            {isDone && <span className="text-sm text-emerald-600 font-semibold">{t('attendance.recorded', { defaultValue: '✓ Recorded' })}</span>}
          </div>
        </section>

        {/* Monthly summary + 36協定 */}
        <section className="mt-6 rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-ink-700">{t('attendance.monthlyAttendance', { defaultValue: 'Monthly attendance' })}</h2>
            <div className="flex items-center gap-2">
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="border border-ink-300 rounded-lg px-2 py-1 text-sm focus:border-accent-500 focus:outline-none">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="border border-ink-300 rounded-lg px-2 py-1 text-sm focus:border-accent-500 focus:outline-none">
                {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {monthData.data && (
            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 border-b border-ink-100">
              <Stat label={t('attendance.daysWorked', { defaultValue: 'Days worked' })} value={String(monthData.data.summary.daysWorked)} />
              <Stat label={t('attendance.totalHours', { defaultValue: 'Total hours' })} value={`${monthData.data.summary.totalWorkedHours}h`} />
              <Stat label={t('attendance.overtimeStat', { defaultValue: 'Overtime (時間外)' })} value={`${monthData.data.summary.overtimeHours}h`} />
            </div>
          )}

          {(monthData.data?.summary.violations ?? []).length > 0 && (
            <div className="px-5 py-3 bg-warn-50 border-b border-warn-500/30">
              <p className="text-xs font-semibold text-warn-700">{t('attendance.overtimeAlerts', { defaultValue: '⚠ 36協定 overtime alerts' })}</p>
              <ul className="mt-1 space-y-0.5">
                {monthData.data!.summary.violations.map((v, i) => (
                  <li key={i} className="text-xs text-warn-600">{v.message}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="min-w-full text-sm">
            <thead className="bg-white text-ink-500">
              <tr>
                <th className="text-start font-medium px-5 py-2">{t('attendance.colDate', { defaultValue: 'Date' })}</th>
                <th className="text-start font-medium px-3 py-2">{t('attendance.colIn', { defaultValue: 'In' })}</th>
                <th className="text-start font-medium px-3 py-2">{t('attendance.colOut', { defaultValue: 'Out' })}</th>
                <th className="text-start font-medium px-3 py-2">{t('attendance.colBreak', { defaultValue: 'Break' })}</th>
                <th className="text-end font-medium px-3 py-2">{t('attendance.colWorked', { defaultValue: 'Worked' })}</th>
                <th className="text-end font-medium px-5 py-2">{t('attendance.colOvertime', { defaultValue: 'Overtime' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {monthData.isLoading && <tr><td colSpan={6} className="text-center text-ink-500 py-8">{t('attendance.loading', { defaultValue: 'Loading…' })}</td></tr>}
              {monthData.data?.records.length === 0 && <tr><td colSpan={6} className="text-center text-ink-500 py-8">{t('attendance.noRecords', { defaultValue: 'No attendance recorded this month.' })}</td></tr>}
              {(monthData.data?.records ?? []).map((r) => (
                <tr key={r.id} className="hover:bg-ink-50/40">
                  <td className="px-5 py-2 num text-ink-900">{r.workDate}</td>
                  <td className="px-3 py-2 num text-ink-700">{hhmm(r.clockInAt)}</td>
                  <td className="px-3 py-2 num text-ink-700">{hhmm(r.clockOutAt)}</td>
                  <td className="px-3 py-2 num text-ink-500">{r.breakMinutes}m</td>
                  <td className="px-3 py-2 num text-end text-ink-900">{hours(r.workedMinutes)}</td>
                  <td className="px-5 py-2 num text-end text-ink-700">{hours(r.overtimeMinutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{label}</p>
      <p className="num text-2xl font-bold text-ink-900">{value}</p>
    </div>
  );
}
